<template>
  <div
    :class="[
      'badiu-tiptap-editor',
      fullscreen ? 'badiu-tiptap-fullscreen' : ''
    ]"
  >
    <!-- Isolated toolbar -->
    <badiu-tiptap-toolbar
      :editor="editor"
      :fullscreen="fullscreen"
      @open-link="openLinkModal"
      @open-image="openImageModal"
      @open-source="openSourceModal"
      @insert-table="insertTable"
      @toggle-fullscreen="toggleFullscreen"
    />

    <!-- Editable content area -->
    <editor-content
      :editor="editor"
      class="badiu-tiptap-content"
    />

    <!-- Status bar (footer) — TinyMCE-style -->
    <div
      v-if="editor"
      class="d-flex justify-content-between align-items-center px-3 py-1 bg-light border-top"
    >
      <span
        v-if="showCounter && editor.storage.characterCount"
        class="text-muted small"
        style="font-variant-numeric: tabular-nums;"
      >
        {{ editor.storage.characterCount.characters() }} characters
        ·
        {{ editor.storage.characterCount.words() }} words
      </span>

      <span class="text-muted small fst-italic ms-auto">
        Badiu Editor
      </span>
    </div>

    <!-- Image modal (isolated component) -->
    <badiu-tiptap-image
      :show="showImageModal"
      :initial="imageInitial"
      :config="config"
      @save="saveImage"
      @cancel="closeImageModal"
      @uploaded="onImageUploaded"
      @error="onUploadError"
    />

    <!-- Link modal (isolated component) -->
    <badiu-tiptap-link
      :show="showLinkModal"
      :initial="linkInitial"
      :config="config"
      @save="saveLink"
      @remove="removeLink"
      @cancel="closeLinkModal"
      @uploaded="onLinkUploaded"
      @error="onUploadError"
    />

    <!-- Source code modal (isolated component) -->
    <badiu-tiptap-source-code
      :show="showSourceModal"
      :html="sourceHtml"
      @save="saveSource"
      @cancel="closeSourceModal"
    />
  </div>
</template>

<script>
/**
 * BadiuTiptapEditor
 *
 * TinyMCE-inspired HTML editor built on Tiptap:
 *  - Toolbar grouped into logical sections (BadiuTiptapToolbar)
 *  - Separate modal for each feature:
 *     · BadiuTiptapImage       → image modal (form + preview)
 *     · BadiuTiptapImageUpload → isolated image upload
 *     · BadiuTiptapFileUpload  → generic file upload (used by link)
 *     · BadiuTiptapLink        → link modal (URL / attachment / target)
 *     · BadiuTiptapSourceCode  → HTML source view / edit modal
 *  - Fullscreen mode
 *  - Paste images as base64
 */
import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import CharacterCount from '@tiptap/extension-character-count'

import BadiuTiptapToolbar from './BadiuTiptapToolbar.vue'
import BadiuTiptapImage from './BadiuTiptapImage.vue'
import BadiuTiptapLink from './BadiuTiptapLink.vue'
import BadiuTiptapSourceCode from './BadiuTiptapSourceCode.vue'

/* ============================================================
 * TextStyle — inline Mark definition.
 * Wraps content in <span style="..."> so that font-family and
 * font-size attributes have a Mark to attach to.
 * Not included in the vendor bundle; built via Underline.extend().
 * ============================================================ */
const TextStyle = Underline.extend({
  name: "textStyle",
  priority: 101,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  parseHTML() {
    return [{ tag: "span", getAttrs: (el) => (el.hasAttribute("style") ? {} : false) }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      removeEmptyTextStyle: () => ({ commands }) => commands.unsetMark(this.name)
    };
  },

  addKeyboardShortcuts() { return {}; },
  addInputRules()        { return []; },
  addPasteRules()        { return []; }
});

/* ============================================================
 * FontFamily — inline Extension definition.
 * Adds a fontFamily attribute to the textStyle mark and exposes
 * setFontFamily / unsetFontFamily commands.
 * Built via TextAlign.extend() to reuse the Extension base class.
 * ============================================================ */
const FontFamily = TextAlign.extend({
  name: "fontFamily",

  addOptions() {
    return { types: ["textStyle"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (el) => el.style.fontFamily?.replace(/['"]+/g, "") || null,
            renderHTML: (attrs) =>
              attrs.fontFamily ? { style: `font-family: ${attrs.fontFamily}` } : {}
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      setFontFamily: (fontFamily) => ({ chain }) =>
        chain().setMark("textStyle", { fontFamily }).run(),
      unsetFontFamily: () => ({ chain }) =>
        chain().setMark("textStyle", { fontFamily: null }).run()
    };
  },

  addKeyboardShortcuts() { return {}; }
});

/* ============================================================
 * FontSize — inline Extension definition.
 * Adds a fontSize attribute to the textStyle mark and exposes
 * setFontSize / unsetFontSize commands.
 * Built via TextAlign.extend() to reuse the Extension base class.
 * ============================================================ */
const FontSize = TextAlign.extend({
  name: "fontSize",

  addOptions() {
    return { types: ["textStyle"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) => el.style.fontSize || null,
            renderHTML: (attrs) =>
              attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) =>
        chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize: () => ({ chain }) =>
        chain().setMark("textStyle", { fontSize: null }).run()
    };
  },

  addKeyboardShortcuts() { return {}; }
});

export default {
  name: "BadiuTiptapEditor",

  components: {
    EditorContent,
    BadiuTiptapToolbar,
    BadiuTiptapImage,
    BadiuTiptapLink,
    BadiuTiptapSourceCode
  },

  props: {
    modelValue: {
      type: String,
      default: ""
    },

    placeholder: {
      type: String,
      default: "Type your content..."
    },

    showCounter: {
      type: Boolean,
      default: true
    },

    /**
     * Runtime configuration passada como objeto simples.
     *
     * Chaves suportadas:
     *   upload_baseurl       URL base do servidor Badiu para uploads
     *                        (ex.: "https://host/path/public").
     *   external_api_vendor  (opcional) Objeto vendor do host que expõe
     *                        Vue e axios (ex.: window.BADIUNET_SYSTEM_CORE_VENDOR).
     *                        Quando fornecido, axios é obtido dele; caso
     *                        contrário usa window.BADIUNET_SYSTEM_CORE_VENDOR
     *                        como fallback ou fica undefined (sem upload).
     *
     * Exemplo:
     *   :config="{ upload_baseurl: 'https://...', external_api_vendor: window.BADIUNET_SYSTEM_CORE_VENDOR }"
     */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: [
    "update:modelValue",
    "image-uploaded",
    "file-uploaded",
    "upload-error"
  ],

  setup(props, { emit }) {
    /* ============================================================
     * Base URL — getter / setter
     *
     * Priority order:
     *   1. Value set programmatically via o setter (_baseUrlOverride)
     *   2. config.upload_baseurl fornecido via prop
     *   3. Default embutido
     * ============================================================ */
    const _baseUrlOverride = ref(null);

    const baseUrl = computed({
      get() {
        return (
          _baseUrlOverride.value ??
          props.config?.upload_baseurl ??
          "https://ds4.badiu21.com.br/badiunet/geral/public"
        );
      },
      set(value) {
        _baseUrlOverride.value = value;
      }
    });

    /* ============================================================
     * Modal state (each modal is its own isolated component)
     * ============================================================ */
    const showImageModal  = ref(false);
    const showLinkModal   = ref(false);
    const showSourceModal = ref(false);

    const imageInitial = reactive({});
    const linkInitial  = reactive({});
    const sourceHtml   = ref("");

    /* ============================================================
     * Fullscreen state
     * ============================================================ */
    const fullscreen = ref(false);

    /* ============================================================
     * Tiptap editor instance
     * ============================================================ */
    const extensions = [
      StarterKit,

      Underline,
      TextStyle,
      FontFamily,
      FontSize,

      Image.configure({
        inline: false,
        allowBase64: true
      }),

      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: null, target: null }
      }),

      Placeholder.configure({
        placeholder: props.placeholder
      }),

      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),

      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,

      TaskList,

      TaskItem.configure({ nested: true }),

      CharacterCount
    ].filter(Boolean);

    const editor = new Editor({
      content: props.modelValue,
      extensions,
      editorProps: {
        handleDoubleClick(view, pos, event) {
          if (event.target && event.target.nodeName === "IMG") {
            openImageModal();
            return true;
          }
          return false;
        },

        /**
         * Handles image paste from the clipboard (screenshots, crops, etc.).
         * Images are inserted as base64 directly — no upload required.
         *
         * HTML images already present in the clipboard (with <img src>)
         * are handled natively by Tiptap via Image.configure({ allowBase64: true }).
         */
        handlePaste(view, event) {
          const clipboard = event.clipboardData;

          if (!clipboard || !clipboard.items) return false;

          for (let i = 0; i < clipboard.items.length; i++) {
            const item = clipboard.items[i];

            if (item.kind === "file" && item.type.startsWith("image/")) {
              const file = item.getAsFile();

              if (!file) continue;

              const reader = new FileReader();

              reader.onload = (e) => {
                const base64 = e.target.result;

                editor
                  .chain()
                  .focus()
                  .setImage({ src: base64 })
                  .run();
              };

              reader.readAsDataURL(file);

              event.preventDefault();
              return true; // paste handled — stop ProseMirror default
            }
          }

          return false; // let ProseMirror handle the paste normally
        }
      },
      onUpdate: ({ editor }) => {
        emit("update:modelValue", editor.getHTML());
      }
    });

    watch(
      () => props.modelValue,
      (value) => {
        if (editor && value !== editor.getHTML()) {
          editor.commands.setContent(value || "", false);
        }
      }
    );

    onBeforeUnmount(() => {
      editor.destroy();
    });

    /* ============================================================
     * IMAGE
     * ============================================================ */
    function openImageModal() {
      const attrs = editor.getAttributes("image") || {};

      imageInitial.src    = attrs.src    || "";
      imageInitial.alt    = attrs.alt    || "";
      imageInitial.width  = attrs.width  || "";
      imageInitial.height = attrs.height || "";

      showImageModal.value = true;
    }

    function closeImageModal() {
      showImageModal.value = false;
    }

    function saveImage(payload) {
      editor.chain().focus().setImage(payload).run();
      closeImageModal();
    }

    function onImageUploaded(payload) {
      emit("image-uploaded", payload);
    }

    /* ============================================================
     * LINK
     * ============================================================ */
    function openLinkModal() {
      const attrs = editor.getAttributes("link") || {};

      const selection     = editor.state.selection;
      const selectedText  = editor.state.doc.textBetween(
        selection.from,
        selection.to,
        " "
      );

      linkInitial.href   = attrs.href   || "";
      linkInitial.target = attrs.target || "";
      linkInitial.title  = attrs.title  || "";
      linkInitial.text   = selectedText || "";

      showLinkModal.value = true;
    }

    function closeLinkModal() {
      showLinkModal.value = false;
    }

    function saveLink(payload) {
      const linkAttrs = { href: payload.href };

      if (payload.target) linkAttrs.target = payload.target;
      if (payload.title)  linkAttrs.title  = payload.title;

      const selection    = editor.state.selection;
      const hasSelection = selection.from !== selection.to;

      const originalSelectedText = hasSelection
        ? editor.state.doc.textBetween(selection.from, selection.to, " ")
        : "";

      const newText    = payload.text || payload.href;
      const textChanged = newText !== originalSelectedText;

      if (hasSelection && !textChanged) {
        // Text unchanged: only apply the link mark.
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink(linkAttrs)
          .run();
      } else if (hasSelection && textChanged) {
        // Text changed: replace the selection with the new text + link mark.
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .deleteSelection()
          .insertContent({
            type: "text",
            text: newText,
            marks: [{ type: "link", attrs: linkAttrs }]
          })
          .run();
      } else {
        // No selection: insert new text with the link mark applied.
        editor
          .chain()
          .focus()
          .insertContent({
            type: "text",
            text: newText,
            marks: [{ type: "link", attrs: linkAttrs }]
          })
          .run();
      }

      closeLinkModal();
    }

    function removeLink() {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .unsetLink()
        .run();

      closeLinkModal();
    }

    function onLinkUploaded(payload) {
      emit("file-uploaded", payload);
    }

    /* ============================================================
     * SOURCE CODE
     * ============================================================ */
    function openSourceModal() {
      sourceHtml.value       = editor.getHTML();
      showSourceModal.value  = true;
    }

    function closeSourceModal() {
      showSourceModal.value = false;
    }

    function saveSource(html) {
      editor.commands.setContent(html || "", true);
      emit("update:modelValue", editor.getHTML());
      closeSourceModal();
    }

    /* ============================================================
     * TABLE
     * ============================================================ */
    function insertTable() {
      editor
        .chain()
        .focus()
        .insertTable({
          rows: 3,
          cols: 3,
          withHeaderRow: true
        })
        .run();
    }

    /* ============================================================
     * FULLSCREEN
     * ============================================================ */
    function toggleFullscreen() {
      fullscreen.value = !fullscreen.value;

      if (fullscreen.value) {
        document.body.classList.add("badiu-tiptap-fullscreen-lock");
      } else {
        document.body.classList.remove("badiu-tiptap-fullscreen-lock");
      }
    }

    /* ============================================================
     * PASTE — base64 images
     * ============================================================
     *
     * The handler lives in editorProps.handlePaste (see editor
     * instantiation above). That is the idiomatic Tiptap approach:
     * it goes through the ProseMirror pipeline instead of relying
     * on DOM events bubbling through the template.
     */

    /* ============================================================
     * ERRORS
     * ============================================================ */
    function onUploadError(payload) {
      emit("upload-error", payload);
    }

    return {
      // Editor
      editor,

      // Configuration
      baseUrl,

      // Modals
      showImageModal,
      showLinkModal,
      showSourceModal,
      imageInitial,
      linkInitial,
      sourceHtml,

      // UI state
      fullscreen,

      // Handlers
      openImageModal,
      closeImageModal,
      saveImage,
      onImageUploaded,

      openLinkModal,
      closeLinkModal,
      saveLink,
      removeLink,
      onLinkUploaded,

      openSourceModal,
      closeSourceModal,
      saveSource,

      insertTable,
      toggleFullscreen,

      onUploadError
    };
  }
};
</script>

<style>
/* ============================================================
 * Editor container
 * ============================================================ */
.badiu-tiptap-editor {
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  overflow: hidden;
  background: #fff;
}

/* ============================================================
 * Editable content area
 * ============================================================ */
.badiu-tiptap-content {
  padding: 14px 16px;
  min-height: 220px;
}

.badiu-tiptap-content .ProseMirror {
  outline: none;
  min-height: 220px;
  line-height: 1.6;
}

.badiu-tiptap-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

/* ============================================================
 * Image node
 * ============================================================ */
.badiu-tiptap-content .ProseMirror img {
  max-width: 100%;
  height: auto;
  cursor: default;
}

.badiu-tiptap-content .ProseMirror img.ProseMirror-selectednode {
  outline: 3px solid #0d6efd;
  outline-offset: 2px;
}

/* ============================================================
 * Table
 * ============================================================ */
.badiu-tiptap-content .ProseMirror table {
  border-collapse: collapse;
  margin: 8px 0;
  table-layout: fixed;
  width: 100%;
}

.badiu-tiptap-content .ProseMirror th,
.badiu-tiptap-content .ProseMirror td {
  border: 1px solid #dee2e6;
  padding: 6px 10px;
  vertical-align: top;
  min-width: 40px;
}

.badiu-tiptap-content .ProseMirror th {
  background: #f8f9fa;
  font-weight: 600;
}

/* ============================================================
 * Fullscreen
 * ============================================================ */
.badiu-tiptap-fullscreen {
  position: fixed !important;
  inset: 0;
  z-index: 9000;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 0 !important;
}

.badiu-tiptap-fullscreen .badiu-tiptap-content {
  flex: 1;
  overflow: auto;
}

body.badiu-tiptap-fullscreen-lock {
  overflow: hidden;
}
</style>
